import { useCallback, useMemo, useState } from "react";
import { Box, Modal } from "@mui/material";
import { FilterOption } from "../../interfaces/FilterOption";
import ModalLoading from "../../views/modal-loading/ModalLoading";
import VariationContentPiecesService from "../../services/variation-content-pieces.service";
import { PostVariation } from "../../interfaces/PostVariation";
import ModalBox from "../../views/modal-box/ModalBox";
import CreateVariationModal from "./create-variation-modal/CreateVariationModal";
import { DataWrapper } from "../../interfaces/DataWrapper";
import { CustomizedVariation } from '../../interfaces/CustomizedVariation';
import { VariationPreferences } from '../../enums/VariationPreferences';
import { Structure, VariationHeadline, VariationType } from '../../enums/VariationCustomization';
import { VariationsModalProps } from '../../interfaces/props/VariationsModalProps';
import NameHelper from '../../helpers/name.helper';
import { Temperature } from '../../enums/Temperature';
import { Relations } from '../../enums/Relations';
import { useNavigate } from 'react-router-dom';
import useUser from '../../hooks/useUser';

const ERROR_MESSAGE = 'Variation service is unavailable right now. Please try again later.'
const INFO_MESSAGE = 'This will take 2-5 min, please wait.'

const VariationsModal = ({ isOpen, onClose, contentPiece }: VariationsModalProps) => {
  const user = useUser();
  const [site, setSite] = useState<FilterOption | null>(null);
  const [language, setLanguage] = useState<FilterOption | null>(null);
  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState<boolean>(false);
  const [isBoxModalOpen, setIsBoxModalOpen] = useState<boolean>(false);
  const [boxLargeMessage, setBoxLargeMessage] = useState<string>('');
  const navigate = useNavigate();
  const [loadingModalMessage, setLoadingModalMessage] = useState<string>('');
  const [customizedVariation, setCustomizedVariation] = useState<CustomizedVariation>({
    [VariationPreferences.REPHRASE_OR_TRANSLATE]: VariationType.DIRECT_TRANSLATION,
    [VariationPreferences.HEADLINE]: VariationHeadline.REGULAR,
    [VariationPreferences.KEEP_WP_BLOCKS]: true,
    [VariationPreferences.STRUCTURE]: Structure.HTML_WITH_BLOCKS,
    [VariationPreferences.TEMPERATURE]: Temperature.DIRECT_TRANSLATION,
  });

  const hasDuplicate: boolean = useMemo(() => {
    if (contentPiece.getVariationContentPieces() && language) {
      const hasDuplicate = contentPiece.getVariationContentPieces().some(variation =>
        NameHelper.getNameFromEntity(variation, Relations.LANGUAGE) === language.name &&
        VariationType.DIRECT_TRANSLATION === variation.attributes.type);

      return hasDuplicate;
    }

    return false;
  }, [contentPiece, language]);

  const onClickDiscardButtonHandler = useCallback(() => {
    setLanguage(null);
    setSite(null);
  }, []);

  const handleSiteChange = useCallback((value: FilterOption | null) => {
    setSite(value);
  }, []);

  const handleCustomizationChange = useCallback((value: CustomizedVariation) => {
    setCustomizedVariation(value);
  }, []);

  const handleLanguageChange = useCallback((value: FilterOption | null) => {
    setLanguage(value);
  }, []);

  const onCloseHandler = useCallback(() => {
    onClose();
  }, [onClose]);

  const onCreateButtonHandler = useCallback(() => {
    setLoadingModalMessage('Creating & Translating')
    setIsLoadingModalOpen(true);

    if (language && site && customizedVariation && user?.email) {
      const variationPost: DataWrapper<PostVariation> = {
        data: {
          contentPiece: contentPiece.getId(),
          content: customizedVariation.structure === Structure.PLAIN_TEXT ? contentPiece.getClearedRawContent() : contentPiece.getRawContent(),
          headlines: contentPiece.getHeadlinesOriginal(),
          language: language,
          site: site,
          type: customizedVariation.rephraseOrTranslate,
          headlinesType: customizedVariation.headlinesType,
          keepWPBlocks: customizedVariation.structure === Structure.PLAIN_TEXT ? false : customizedVariation.keepWPBlocks,
          length: customizedVariation.textLength ?? '',
          structure: customizedVariation.structure,
          temperature: hasDuplicate && customizedVariation.rephraseOrTranslate === VariationType.DIRECT_TRANSLATION ? Temperature.DUPLICATE_TRANSLATION : customizedVariation.temperature,
          user: user.email,
          originalLanguage: contentPiece.getLanguageId()
        }
      };

      VariationContentPiecesService.create(variationPost)
        .then((variation) => {
          navigate(`/variation/${variation.id}`);
        })
        .catch((error) => {
          setIsLoadingModalOpen(false);
          setBoxLargeMessage(error.message ?? ERROR_MESSAGE);
          setIsBoxModalOpen(true);
        });
    }
  }, [contentPiece, language, site, customizedVariation, hasDuplicate, navigate, user]);

  const renderModal = () => {
    if (isLoadingModalOpen) {
      return <Box className='disable-mouse-click'>
        <ModalLoading message={loadingModalMessage} infoMessage={INFO_MESSAGE} />
      </Box>
    }

    if (isBoxModalOpen) {
      return <ModalBox largeMessage={boxLargeMessage} smallMessage={''} showSuccessIcon={false} />
    }

    return <CreateVariationModal
      site={site}
      language={language}
      customizeVariation={customizedVariation}
      variations={contentPiece.getVariationContentPieces()}
      onSiteChange={handleSiteChange}
      onLanguageChange={handleLanguageChange}
      onCustomizeChange={handleCustomizationChange}
      onDiscard={onClickDiscardButtonHandler}
      onCreate={onCreateButtonHandler}
    />;
  }

  return (
    <Modal open={isOpen} onClose={onCloseHandler}>
      <>
        {renderModal()}
      </>
    </Modal>
  )
};

export default VariationsModal;
